import { AlignCenter, AlignJustify, AlignLeft, AlignRight } from 'lucide-react'
import { UnderlineIcon } from '@components/ui/custom-icons'
import { keepTextEditFocus } from '../handlers/keep-text-edit-focus'
import type { AlignmentAndDecoration } from '../hooks/use-alignment-and-decoration'
import type { AlignmentUiValue } from '../types/alignment-ui-value'
import { useClearSelectionCommand } from '../hooks/use-clear-selection-command'
import { IconToggleButton } from './icon-toggle-button'
import { SectionHeading } from './section-heading'

const ALIGNMENT_OPTIONS: ReadonlyArray<{
  value: AlignmentUiValue
  icon: typeof AlignLeft
  label: string
}> = [
  { value: 'left', icon: AlignLeft, label: 'Align Left' },
  { value: 'center', icon: AlignCenter, label: 'Align Center' },
  { value: 'right', icon: AlignRight, label: 'Align Right' },
  { value: 'justify', icon: AlignJustify, label: 'Justify' },
]

/** Alignment and Formatting: the group heading, Align buttons and the Underline decoration.
 *  Renders siblings (a Fragment) so each stays a direct child of the panel's spaced column. */
export function AlignmentSection({ field }: { field: AlignmentAndDecoration }) {
  const clearSelection = useClearSelectionCommand()

  return (
    <>
      {/* Alignment and Formatting Section */}
      <div>
        <SectionHeading>Alignment and Formatting</SectionHeading>
      </div>

      {/* Align Section */}
      <div className="space-y-3" onPointerDown={keepTextEditFocus}>
        <label className="text-sm font-medium text-foreground leading-normal mt-2">Align</label>
        <div role="group" aria-label="Text alignment" className="flex gap-1 mt-2">
          {ALIGNMENT_OPTIONS.map((option) => (
            <IconToggleButton
              key={option.value}
              icon={option.icon}
              label={option.label}
              pressed={field.alignment === option.value}
              disabled={field.alignmentControlsDisabled}
              onClick={() => field.selectAlignment(option.value)}
              onEscape={clearSelection}
            />
          ))}
        </div>
      </div>

      {/* Decoration Section */}
      <div className="space-y-3" onPointerDown={keepTextEditFocus}>
        <label className="text-sm font-medium text-foreground leading-normal">Decoration</label>
        <div className="flex gap-1 mt-2">
          <IconToggleButton
            icon={UnderlineIcon}
            label="Underline"
            pressed={field.underlineState}
            disabled={field.alignmentControlsDisabled}
            onClick={field.toggleUnderline}
            onEscape={clearSelection}
          />
        </div>
      </div>
    </>
  )
}
